import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '@fluentui/react-hooks';
import { PrimaryButton, SearchBox as LegacySearchBox, Dropdown, IDropdownOption } from '@fluentui/react';
import {
    Button,
    Spinner,
    SearchBox as FluentSearchBox,
    Badge,
    Tooltip,
    Tab,
    TabList,
} from '@fluentui/react-components';
import {
    Add24Regular,
    EditRegular,
    DeleteRegular,
    ArrowSortDownRegular,
    ArrowSortRegular,
    ArrowSortUpRegular,
    BuildingMultiple20Regular,
    Layer20Regular,
    AppsListDetail20Regular,
    ProhibitedRegular,
    FullScreenMaximize20Regular,
    FullScreenMinimize20Regular,
    Column20Regular,
    ColumnTriple20Regular,
    Eye20Regular,
    EyeOff20Regular,
} from '@fluentui/react-icons';

import ConceptFormComponent from '../ConceptForm/ConceptForm.Component';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';

import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { NewContractConceptPaymentSchedule } from '../../../../models/Entities/Contracts/NewContractConceptPaymentSchedule';

import * as Fn from './ConceptGroupedView.Functions';
import {
    getGroupForConcept,
    getSettlementLabel,
    getValuationLabel,
    formatCurrencyAmount,
    formatCoefficient,
    formatPlainAmount,
    resolveAmountCurrency,
    isGroupedInAnyStage,
    getStagesForBranch,
    isSharedStage,
    isOptionalConcept,
    conceptsForTotals,
    ConceptValuationOptionsEnum,
    SettlementOptionsEnum,
} from './ConceptGroupedView.Functions';
import { conceptGridSheets } from './ConceptGroupedView.Excel';
import ExcelExportButton from '../../statistics/ExcelExportButton.Component';
import { buildFileName, downloadWorkbook } from '../../statistics/StatisticsExcel.Functions';

// Reglas de precio de la matriz por etapa (precio unitario, coeficiente,
// cantidad y el total que sale de los tres).
import {
    ExecutionKey,
    allExecutions,
    conceptApplies,
    getStageScheduleFor,
    getCoefficientForExecution,
    getQuantityForStage,
    usesConceptFixedValues,
    getUnitPriceFor,
    isRemoteStage,
    isFixedValueByStage,
    getRelevantExecutionFor,
    getEffectiveCoefficient,
    getExecutionTotalFor,
    getConceptTotalForStage,
    getDisplayedCellTotal,
} from './ConceptGroupedView.Pricing';

import './ConceptGroupedView.Styles.css';

type SortMode = 'load' | 'alpha-asc' | 'alpha-desc';

const sortConcepts = (concepts: ContractConcept[], mode: SortMode): ContractConcept[] => {
    if (mode === 'load') return concepts;
    const sorted = [...concepts].sort((a, b) => {
        const an = (a.concept?.conceptName || '').toString();
        const bn = (b.concept?.conceptName || '').toString();
        return an.localeCompare(bn, undefined, { sensitivity: 'base', numeric: true });
    });
    return mode === 'alpha-asc' ? sorted : sorted.reverse();
};

const SortToggle: React.FC<{ mode: SortMode; onChange: (m: SortMode) => void; t: any }> = ({ mode, onChange, t }) => {
    const cycle = () => {
        if (mode === 'load') onChange('alpha-asc');
        else if (mode === 'alpha-asc') onChange('alpha-desc');
        else onChange('load');
    };
    let label: string;
    let icon: React.ReactElement;
    if (mode === 'load') {
        label = 'Orden de carga';
        icon = <ArrowSortRegular />;
    } else if (mode === 'alpha-asc') {
        label = 'A → Z';
        icon = <ArrowSortDownRegular />;
    } else {
        label = 'Z → A';
        icon = <ArrowSortUpRegular />;
    }
    return (
        <Tooltip content={`Ordenar (${label})`} relationship='label' withArrow>
            <Button
                appearance='subtle'
                size='small'
                icon={icon}
                onClick={cycle}
                className='cgv-sort-btn'
            >
                {label}
            </Button>
        </Tooltip>
    );
};

// ──────────────────────────────────────────────────────────
// Full screen toggle for a grid section
// ──────────────────────────────────────────────────────────
// While expanded the card is taken out of the flow (fixed, viewport sized),
// the page scroll is locked and Escape restores it.
const useFullScreenSection = () => {
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        if (!isExpanded) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsExpanded(false);
        };

        document.body.classList.add('cgv-body--locked');
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.classList.remove('cgv-body--locked');
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isExpanded]);

    return {
        isExpanded,
        toggleExpanded: () => setIsExpanded(current => !current),
    };
};

const ExpandToggle: React.FC<{ expanded: boolean; onToggle: () => void }> = ({ expanded, onToggle }) => (
    <Tooltip
        content={expanded ? 'Restaurar tamaño (Esc)' : 'Ampliar a pantalla completa'}
        relationship='label'
        withArrow
    >
        <Button
            appearance='subtle'
            size='small'
            icon={expanded ? <FullScreenMinimize20Regular /> : <FullScreenMaximize20Regular />}
            onClick={onToggle}
            aria-pressed={expanded}
            className='cgv-expand-btn'
        />
    </Tooltip>
);

const CompactToggle: React.FC<{ compact: boolean; onToggle: () => void }> = ({ compact, onToggle }) => (
    <Tooltip
        content={compact
            ? 'Ver todas las modalidades'
            : 'Ver sólo la modalidad que liquida cada etapa'}
        relationship='label'
        withArrow
    >
        <Button
            appearance='subtle'
            size='small'
            icon={compact ? <ColumnTriple20Regular /> : <Column20Regular />}
            onClick={onToggle}
            aria-pressed={compact}
            className='cgv-compact-btn'
        />
    </Tooltip>
);

// Incluir o no los conceptos opcionales en los totales. Sin opcionales, la grilla
// los sigue mostrando en gris: se ve qué quedó afuera en lugar de desaparecerlo.
const OptionalToggle: React.FC<{ include: boolean; disabled: boolean; onToggle: () => void }> = ({ include, disabled, onToggle }) => (
    <Tooltip
        content={disabled
            ? 'No hay conceptos opcionales en esta grilla'
            : include
                ? 'Los opcionales suman a los totales. Tocá para excluirlos'
                : 'Los opcionales se muestran en gris y no suman. Tocá para incluirlos'}
        relationship='label'
        withArrow
    >
        <Button
            appearance='subtle'
            size='small'
            icon={include ? <Eye20Regular /> : <EyeOff20Regular />}
            onClick={onToggle}
            disabled={disabled}
            aria-pressed={!include}
            className='cgv-optional-btn'
        >
            {include ? 'Con opcionales' : 'Sin opcionales'}
        </Button>
    </Tooltip>
);

const matchesText = (cc: ContractConcept, term: string): boolean => {
    if (!term) return true;
    const n = (cc.concept?.conceptName || '').toString().toLowerCase();
    const d = (cc.concept?.conceptDescription || '').toString().toLowerCase();
    const r = (cc.concept?.conceptReferenceId as any || '').toString().toLowerCase();
    return n.includes(term) || d.includes(term) || r.includes(term);
};


// ──────────────────────────────────────────────────────────
// Currency
// ──────────────────────────────────────────────────────────
// The concept currency is the authority: ConceptForm writes conceptCurrency and
// every paymentSchedule[].currency from the SAME field, so the per-stage value is
// just a denormalised copy — when they disagree the schedule row is stale (saved
// before the concept currency was changed) and must not win.
// Order: concept → schedule → contract. Nothing is assumed to be ARS.
const getAmountCurrency = (
    sch: NewContractConceptPaymentSchedule | undefined,
    cc: ContractConcept,
    contractCurrency: string | undefined,
): string | undefined => resolveAmountCurrency(cc.conceptCurrency, sch?.currency, contractCurrency);

// Amounts are only ever added together within the same currency.
type AmountsByCurrency = Map<string, number>;

const addAmount = (bucket: AmountsByCurrency, currency: string | undefined, amount: number): void => {
    const key = currency || '';
    bucket.set(key, (bucket.get(key) || 0) + amount);
};

// Dos sumas por moneda iguales. Tolera el redondeo de sumar decimales.
const sameAmounts = (a: AmountsByCurrency, b: AmountsByCurrency): boolean => {
    if (a.size !== b.size) return false;
    return Array.from(a.entries()).every(([currencyCode, amount]) =>
        b.has(currencyCode) && Math.abs((b.get(currencyCode) || 0) - amount) < 0.005);
};

const CurrencyAmounts: React.FC<{ amounts: AmountsByCurrency; bold?: boolean }> = ({ amounts, bold }) => {
    const entries = Array.from(amounts.entries());
    if (entries.length === 0) return <span className='cgv-muted'>—</span>;
    return (
        <span className='cgv-amount-stack'>
            {entries.map(([currencyCode, amount]) => {
                const text = formatCurrencyAmount(amount, currencyCode || undefined);
                return (
                    <span key={currencyCode || 'unknown'} className='cgv-cell-price'>
                        {bold ? <strong>{text}</strong> : text}
                    </span>
                );
            })}
        </span>
    );
};

// ──────────────────────────────────────────────────────────
// Branch resolution (concepts ↔ branches)
// ──────────────────────────────────────────────────────────
const resolveBranchId = (branch: any): number | undefined => {
    const rawId = branch?.branchId ?? branch?.id;
    const parsedId = Number(rawId);
    return Number.isFinite(parsedId) ? parsedId : undefined;
};

const getConceptBranchIds = (concept: ContractConcept): number[] => {
    const ids = new Set<number>();

    (concept.branches || []).forEach((branch) => {
        const id = resolveBranchId(branch);
        if (id !== undefined) {
            ids.add(id);
        }
    });

    const directCandidates = [
        (concept as any)?.branchId,
        (concept as any)?.branchid,
        (concept as any)?.concept?.branchId,
        (concept as any)?.concept?.branchid,
        (concept as any)?.concept?.branch?.id,
        (concept as any)?.concept?.branch?.branchId,
    ];

    directCandidates.forEach((raw) => {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) {
            ids.add(parsed);
        }
    });

    return Array.from(ids);
};

// A concept with no branch association applies to every branch.
const conceptAppliesToBranch = (cc: ContractConcept, branchId: number | undefined): boolean => {
    const ids = getConceptBranchIds(cc);
    if (ids.length === 0) return true;
    if (branchId === undefined) return true;
    return ids.includes(branchId);
};

// ──────────────────────────────────────────────────────────
// Generic (non-ByStage) table
// ──────────────────────────────────────────────────────────
interface GenericTableProps {
    title: string;
    icon: React.ReactNode;
    accent: string;
    concepts: ContractConcept[];
    currency?: string;
    onEdit: (c: ContractConcept) => void;
    onDelete: (c: ContractConcept) => void;
}

const GenericTable: React.FC<GenericTableProps> = ({ title, icon, accent, concepts, currency, onEdit, onDelete }) => {
    const [filter, setFilter] = useState('');
    const [sortMode, setSortMode] = useState<SortMode>('load');
    const { isExpanded, toggleExpanded } = useFullScreenSection();
    const { t } = useTranslation();

    const visible = useMemo(() => {
        const term = filter.trim().toLowerCase();
        return sortConcepts(concepts.filter(c => matchesText(c, term)), sortMode);
    }, [concepts, filter, sortMode]);

    return (
        <section className={`cgv-card${isExpanded ? ' cgv-card--expanded' : ''}`} style={{ ['--cgv-accent' as any]: accent }}>
            <header className='cgv-card__header'>
                <span className='cgv-card__icon'>{icon}</span>
                <h3 className='cgv-card__title'>{title}</h3>
                <span className='cgv-card__count'>{concepts.length}</span>
                <div className='cgv-card__actions'>
                    <SortToggle mode={sortMode} onChange={setSortMode} t={t} />
                    <ExpandToggle expanded={isExpanded} onToggle={toggleExpanded} />
                    <FluentSearchBox
                        size='small'
                        placeholder='Buscar…'
                        value={filter}
                        onChange={(_, d) => setFilter(d.value || '')}
                        className='cgv-search'
                    />
                </div>
            </header>

            {visible.length === 0
                ? <div className='cgv-empty'>Sin conceptos en esta categoría.</div>
                : (
                    <div className='cgv-table-wrap'>
                        <table className='cgv-table'>
                            <thead>
                                <tr>
                                    <th className='cgv-th cgv-th--name'>Concepto</th>
                                    <th className='cgv-th'>Liquidación</th>
                                    <th className='cgv-th'>Valuación</th>
                                    <th className='cgv-th cgv-th--num'>Precio</th>
                                    <th className='cgv-th'>Ejecución</th>
                                    <th className='cgv-th cgv-th--actions'>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(cc => (
                                    <tr key={cc.id} className='cgv-tr'>
                                        <td className='cgv-td cgv-td--name'>
                                            <div className='cgv-concept'>
                                                <span className='cgv-concept__name'>{cc.concept?.conceptName || '—'}</span>
                                                {cc.concept?.conceptReferenceId && (
                                                    <span className='cgv-concept__ref'>#{cc.concept.conceptReferenceId as any}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className='cgv-td'>
                                            <Badge appearance='outline' size='small'>{getSettlementLabel(cc.settlementSetting)}</Badge>
                                        </td>
                                        <td className='cgv-td'>
                                            <Badge appearance='tint' size='small'>{getValuationLabel(cc.valuationType)}</Badge>
                                        </td>
                                        <td className='cgv-td cgv-td--num cgv-amount'>
                                            {formatCurrencyAmount(cc.priceValue, resolveAmountCurrency(cc.conceptCurrency, currency))}
                                        </td>
                                        <td className='cgv-td'>
                                            <div className='cgv-exec'>
                                                {cc.applyToInFaceExecution && <span className='cgv-exec__pill cgv-exec__pill--inFace' title='Presencial'>Pres.</span>}
                                                {cc.applyToRemoteExecution && <span className='cgv-exec__pill cgv-exec__pill--remote' title='Remoto'>Rem.</span>}
                                                {cc.applyToPhoneExecution && <span className='cgv-exec__pill cgv-exec__pill--phone' title='Telefónico'>Tel.</span>}
                                            </div>
                                        </td>
                                        <td className='cgv-td cgv-td--actions'>
                                            <Button appearance='subtle' size='small' icon={<EditRegular />} aria-label='Editar' onClick={() => onEdit(cc)} />
                                            <Button appearance='subtle' size='small' icon={<DeleteRegular />} aria-label='Eliminar' onClick={() => onDelete(cc)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
        </section>
    );
};

// ──────────────────────────────────────────────────────────
// ByStage matrix table (sticky concept column + scrollable stages)
// ──────────────────────────────────────────────────────────
interface ByStageTableProps {
    title: string;
    accent: string;
    concepts: ContractConcept[];
    stages: ContractStage[];
    branches: ContractBranch[];
    currency?: string;
    /** Si los opcionales suman. Vive afuera porque la exportación también lo necesita. */
    includeOptional: boolean;
    onIncludeOptionalChange: (include: boolean) => void;
    onEdit: (c: ContractConcept) => void;
    onDelete: (c: ContractConcept) => void;
}

interface BranchTabDef {
    key: string;
    label: string;
    branchId?: number;
    stageCount: number;
}

const UNASSIGNED_BRANCH_TAB = 'unassigned';

const ByStageTable: React.FC<ByStageTableProps> = ({
    title,
    accent,
    concepts,
    stages,
    branches,
    currency,
    includeOptional,
    onIncludeOptionalChange,
    onEdit,
    onDelete,
}) => {
    const [filter, setFilter] = useState('');
    const [sortMode, setSortMode] = useState<SortMode>('load');
    const [selectedBranchTab, setSelectedBranchTab] = useState<string>(UNASSIGNED_BRANCH_TAB);
    const hasOptionalConcepts = useMemo(() => concepts.some(isOptionalConcept), [concepts]);
    const { isExpanded, toggleExpanded } = useFullScreenSection();
    const { t } = useTranslation();

    const orderedStages = useMemo(() => {
        return [...(stages || [])].sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0));
    }, [stages]);

    // One tab per branch — each tab shows the stages of that branch and the
    // prices that apply to it.
    const branchTabs = useMemo<BranchTabDef[]>(() => {
        const countByBranch = new Map<string, number>();
        orderedStages.forEach((stage) => {
            const key = stage.branchId === undefined || stage.branchId === null
                ? UNASSIGNED_BRANCH_TAB
                : `branch-${stage.branchId}`;
            countByBranch.set(key, (countByBranch.get(key) || 0) + 1);
        });

        const namesById = new Map<number, string>();
        (branches || []).forEach((branch) => {
            namesById.set(branch.id, (branch.branchName || '').trim() || `Rama #${branch.id}`);
        });
        orderedStages.forEach((stage) => {
            if (stage.branchId === undefined || stage.branchId === null) return;
            if (!namesById.has(stage.branchId)) {
                namesById.set(stage.branchId, `Rama #${stage.branchId}`);
            }
        });

        const tabs: BranchTabDef[] = Array.from(namesById.entries())
            .map(([id, name]) => ({
                key: `branch-${id}`,
                label: name,
                branchId: id,
                // El contador dice cuántas visitas se ven en la solapa, y ahí
                // entran las compartidas: si contara sólo las propias, el número
                // no coincidiría con las columnas de la tabla.
                stageCount: getStagesForBranch(orderedStages, id).length,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }));

        const unassignedCount = countByBranch.get(UNASSIGNED_BRANCH_TAB) || 0;
        if (unassignedCount > 0 || tabs.length === 0) {
            tabs.unshift({
                key: UNASSIGNED_BRANCH_TAB,
                label: tabs.length === 0 ? 'Todas las etapas' : 'Sin rama',
                stageCount: unassignedCount,
            });
        }

        return tabs;
    }, [orderedStages, branches]);

    useEffect(() => {
        if (branchTabs.length > 0 && !branchTabs.some((tab) => tab.key === selectedBranchTab)) {
            setSelectedBranchTab(branchTabs[0].key);
        }
    }, [branchTabs, selectedBranchTab]);

    const activeBranchId = useMemo(() => {
        return branchTabs.find((tab) => tab.key === selectedBranchTab)?.branchId;
    }, [branchTabs, selectedBranchTab]);

    // Columnas de la rama elegida, con las etapas sin rama incluidas: aplican a
    // todas, así que también se liquidan acá y tienen que sumar a los totales.
    const stageColumns = useMemo(
        () => getStagesForBranch(orderedStages, activeBranchId),
        [orderedStages, activeBranchId],
    );

    const visible = useMemo(() => {
        const term = filter.trim().toLowerCase();
        return sortConcepts(
            concepts.filter(c => matchesText(c, term) && conceptAppliesToBranch(c, activeBranchId)),
            sortMode,
        );
    }, [concepts, filter, sortMode, activeBranchId]);

    // Dos sumas distintas, separadas por moneda para no mezclar importes:
    //  - stageTotals: lo que muestra la fila de totales en cada columna, una por
    //    modalidad. Es la suma de lo que se ve en esa columna.
    //  - relevantStageTotals: sólo la modalidad que liquida cada etapa. Es lo que
    //    arma el total general y lo que muestra la vista compacta.
    const totals = useMemo(() => {
        const stageTotals = new Map<string, AmountsByCurrency>();
        const relevantStageTotals = new Map<string, AmountsByCurrency>();
        const grandTotal: AmountsByCurrency = new Map();
        let totalQuantity = 0;

        const addTo = (target: Map<string, AmountsByCurrency>, key: string, currencyCode: string | undefined, amount: number) => {
            if (!target.has(key)) target.set(key, new Map());
            addAmount(target.get(key)!, currencyCode, amount);
        };

        conceptsForTotals(visible, includeOptional).forEach((cc) => {
            stageColumns.forEach((stage) => {
                const schedule = getStageScheduleFor(cc, stage.id);
                if (!schedule && !isGroupedInAnyStage(cc)) {
                    return;
                }

                totalQuantity += getQuantityForStage(schedule);

                const cellCurrency = getAmountCurrency(schedule, cc, currency);

                const execution = getRelevantExecutionFor(cc, schedule, stage);
                const relevantTotal = getExecutionTotalFor(cc, schedule, execution);
                addTo(relevantStageTotals, `${stage.id}-${execution}`, cellCurrency, relevantTotal);
                addAmount(grandTotal, cellCurrency, relevantTotal);

                allExecutions.forEach((ex) => {
                    const shown = getDisplayedCellTotal(cc, schedule, ex.key);
                    if (shown === undefined) return;
                    addTo(stageTotals, `${stage.id}-${ex.key}`, cellCurrency, shown);
                });
            });
        });

        return { stageTotals, relevantStageTotals, grandTotal, totalQuantity };
    }, [visible, stageColumns, currency, includeOptional]);

    // Compact view: one column per stage instead of three, showing only the
    // modality that stage actually settles.
    const [isCompact, setIsCompact] = useState(false);

    // Which modality each stage collapses to. A presential stage is always
    // in-face; a remote one depends on the data — phone when loaded, otherwise
    // remote — and different concepts of the same stage may differ, so the
    // header can end up naming both.
    const compactExecutionsByStage = useMemo(() => {
        const byStage = new Map<number, ExecutionKey[]>();

        stageColumns.forEach((stage) => {
            if (!isRemoteStage(stage)) {
                byStage.set(stage.id, ['inFace']);
                return;
            }

            const used = new Set<ExecutionKey>();
            visible.forEach((cc) => {
                const schedule = getStageScheduleFor(cc, stage.id);
                if (!schedule && !isGroupedInAnyStage(cc)) return;
                used.add(getRelevantExecutionFor(cc, schedule, stage));
            });

            byStage.set(
                stage.id,
                used.size > 0
                    ? allExecutions.filter((ex) => used.has(ex.key)).map((ex) => ex.key)
                    : ['remote']
            );
        });

        return byStage;
    }, [stageColumns, visible]);

    const hasBranchTabs = branchTabs.length > 1;

    // Both header rows are sticky. The modality row has to stop right below the
    // stage-name row instead of on top of it, so its offset is the measured
    // height of that row (font size / wrapping can change it).
    const stageHeadRowRef = React.useRef<HTMLTableRowElement>(null);
    const [stageHeadHeight, setStageHeadHeight] = useState(0);

    useEffect(() => {
        const node = stageHeadRowRef.current;
        if (!node) return;

        const measure = () => setStageHeadHeight(node.getBoundingClientRect().height);
        measure();

        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [stageColumns, isExpanded, isCompact, visible.length]);

    return (
        <section className={`cgv-card cgv-card--matrix${isExpanded ? ' cgv-card--expanded' : ''}`} style={{ ['--cgv-accent' as any]: accent }}>
            <header className='cgv-card__header'>
                <span className='cgv-card__icon'><Layer20Regular /></span>
                <h3 className='cgv-card__title'>{title}</h3>
                <span className='cgv-card__count'>{concepts.length}</span>
                <div className='cgv-card__actions'>
                    <SortToggle mode={sortMode} onChange={setSortMode} t={t} />
                    <span className='cgv-actions-sep' role='separator' aria-orientation='vertical' />
                    <OptionalToggle
                        include={includeOptional}
                        disabled={!hasOptionalConcepts}
                        onToggle={() => onIncludeOptionalChange(!includeOptional)}
                    />
                    <span className='cgv-actions-sep' role='separator' aria-orientation='vertical' />
                    <CompactToggle compact={isCompact} onToggle={() => setIsCompact(v => !v)} />
                    <ExpandToggle expanded={isExpanded} onToggle={toggleExpanded} />
                    <FluentSearchBox
                        size='small'
                        placeholder='Buscar…'
                        value={filter}
                        onChange={(_, d) => setFilter(d.value || '')}
                        className='cgv-search'
                    />
                </div>
            </header>

            {hasBranchTabs && (
                <TabList
                    className='cgv-branch-tabs'
                    selectedValue={selectedBranchTab}
                    onTabSelect={(_, data) => setSelectedBranchTab(String(data.value))}
                >
                    {branchTabs.map((tab) => (
                        <Tab key={tab.key} value={tab.key}>
                            <span className='cgv-branch-tab'>
                                <span className='cgv-branch-tab__label'>{tab.label}</span>
                                <span className='cgv-branch-tab__badge'>{tab.stageCount}</span>
                            </span>
                        </Tab>
                    ))}
                </TabList>
            )}

            <div className='cgv-modality-legend'>
                Cada celda muestra lo que se liquida en esa visita: <strong>precio × coeficiente × cantidad</strong>.
                Debajo del total aparecen los factores que no son 1. La fila de totales muestra el total de cada
                modalidad, pero al total general sólo suma la modalidad de cada etapa —resaltada—: presencial, o
                telefónico —remoto si no hay telefónico— en las etapas remotas.
                {!includeOptional && hasOptionalConcepts && (
                    <> Los conceptos <strong>opcionales</strong> se muestran en gris y no suman a los totales.</>
                )}
            </div>

            {stageColumns.length === 0
                ? <div className='cgv-empty'>Esta rama no tiene etapas configuradas.</div>
                : visible.length === 0
                    ? <div className='cgv-empty'>Sin conceptos por etapa para esta rama.</div>
                    : (
                        <div className='cgv-matrix-wrap'>
                            <table className='cgv-matrix'>
                                <thead>
                                    <tr ref={stageHeadRowRef}>
                                        <th className='cgv-th cgv-th--name cgv-sticky-col'>Concepto</th>
                                        <th className='cgv-th cgv-th--num cgv-sticky-col cgv-sticky-col--2'>Precio base</th>
                                        {stageColumns.map(st => (
                                            <th
                                                key={st.id}
                                                className='cgv-th cgv-th--stage'
                                                colSpan={isCompact ? 1 : 3}
                                            >
                                                <div className='cgv-stage-head'>
                                                    <span className='cgv-stage-head__name' title={st.stageName}>{st.stageName}</span>
                                                    <span className='cgv-stage-head__tags'>
                                                        <span className={`cgv-stage-head__mode cgv-stage-head__mode--${isRemoteStage(st) ? 'remote' : 'inFace'}`}>
                                                            {isRemoteStage(st) ? 'Remota' : 'Presencial'}
                                                        </span>
                                                        {/* La visita sin rama corre para todas: dentro de una rama hay
                                                            que poder distinguirla de las propias, porque su importe
                                                            suma acá y también en las demás solapas. */}
                                                        {activeBranchId !== undefined && isSharedStage(st) && (
                                                            <span
                                                                className='cgv-stage-head__shared'
                                                                title='Esta visita no pertenece a la rama: aplica a todas las ramas del contrato.'
                                                            >
                                                                Todas las ramas
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                        <th className='cgv-th cgv-th--num cgv-th--quantity'>Cantidad</th>
                                        <th className='cgv-th cgv-th--num cgv-th--total'>Total a liquidar</th>
                                        <th className='cgv-th cgv-th--actions cgv-sticky-col-right'>Acciones</th>
                                    </tr>
                                    <tr className='cgv-subhead' style={{ ['--cgv-subhead-top' as any]: `${stageHeadHeight}px` }}>
                                        <th className='cgv-th cgv-sticky-col' />
                                        <th className='cgv-th cgv-sticky-col cgv-sticky-col--2' />
                                        {stageColumns.flatMap(st => {
                                            if (isCompact) {
                                                const keys = compactExecutionsByStage.get(st.id) || ['inFace'];
                                                const label = allExecutions
                                                    .filter((ex) => keys.includes(ex.key))
                                                    .map((ex) => ex.short)
                                                    .join(' / ');
                                                return [(
                                                    <th
                                                        key={`${st.id}-compact`}
                                                        className={`cgv-th cgv-th--exec cgv-th--exec-${keys[0]} cgv-cell--stage-start`}
                                                    >
                                                        {label}
                                                    </th>
                                                )];
                                            }
                                            return allExecutions.map((ex, exIndex) => (
                                                <th key={`${st.id}-${ex.key}`} className={`cgv-th cgv-th--exec cgv-th--exec-${ex.key}${exIndex === 0 ? ' cgv-cell--stage-start' : ''}`}>{ex.short}</th>
                                            ));
                                        })}
                                        <th className='cgv-th cgv-th--quantity' />
                                        <th className='cgv-th cgv-th--total' />
                                        <th className='cgv-th cgv-sticky-col-right' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map(cc => {
                                        // Cell render mode per concept:
                                        //  - ValueByStage           → show price per stage, hide base price
                                        //  - FixedValue + ByStage   → show coefficient pill per stage, base price visible
                                        //  - otherwise (has schedule) → show price per stage, base price visible
                                        const isValueByStage = cc.valuationType === ConceptValuationOptionsEnum.ValueByStage;
                                        const cellMode: 'price' | 'coefficient' = isFixedValueByStage(cc) ? 'coefficient' : 'price';
                                        const isExcluded = !includeOptional && isOptionalConcept(cc);
                                        return (
                                            <tr
                                                key={cc.id}
                                                className={`cgv-tr${isExcluded ? ' cgv-tr--excluded' : ''}`}
                                                title={isExcluded ? 'Concepto opcional: no suma a los totales' : undefined}
                                            >
                                                <td className='cgv-td cgv-td--name cgv-sticky-col'>
                                                    <div className='cgv-concept'>
                                                        <span className='cgv-concept__name'>{cc.concept?.conceptName || '—'}</span>
                                                        <span className='cgv-concept__meta'>
                                                            <Badge appearance='outline' size='small'>{getSettlementLabel(cc.settlementSetting)}</Badge>
                                                            <Badge appearance='tint' size='small'>{getValuationLabel(cc.valuationType)}</Badge>
                                                            {isOptionalConcept(cc) && (
                                                                <Badge appearance='tint' color='warning' size='small'>Opcional</Badge>
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className='cgv-td cgv-td--num cgv-sticky-col cgv-sticky-col--2 cgv-amount'>
                                                    {isValueByStage
                                                        ? <span className='cgv-muted'>—</span>
                                                        : formatCurrencyAmount(cc.priceValue, resolveAmountCurrency(cc.conceptCurrency, currency))}
                                                </td>
                                                {stageColumns.flatMap(st => {
                                                    const sch = getStageScheduleFor(cc, st.id);
                                                    const relevantExecution = getRelevantExecutionFor(cc, sch, st);
                                                    // compact: this row shows its own settled modality, so a
                                                    // stage where concepts differ still reads correctly
                                                    const rowExecutions = isCompact
                                                        ? allExecutions.filter((ex) => ex.key === relevantExecution)
                                                        : allExecutions;
                                                    return rowExecutions.map((ex, exIndex) => {
                                                        const stageStartClass = exIndex === 0 ? ' cgv-cell--stage-start' : '';
                                                        // For ByStage concepts the applyTo* flags may not be set;
                                                        // if a schedule entry exists for this stage, treat all executions as applicable.
                                                        const applies = usesConceptFixedValues(cc, sch)
                                                            ? conceptApplies(cc, ex.key)
                                                            : (!!sch || conceptApplies(cc, ex.key));
                                                        if (!applies) {
                                                            return <td key={`${st.id}-${ex.key}`} className={`cgv-td cgv-td--cell cgv-td--cell-${ex.key}${stageStartClass} cgv-cell--na`} title='No aplica'>·</td>;
                                                        }
                                                        const isRelevant = ex.key === relevantExecution;
                                                        const relevanceClass = isRelevant ? 'cgv-cell--relevant' : 'cgv-cell--secondary';
                                                        const relevanceTitle = isRelevant
                                                            ? `${ex.label} — modalidad liquidada en esta etapa`
                                                            : `${ex.label} — no suma al total de esta etapa`;
                                                        const cellClassName = `cgv-td cgv-td--cell cgv-td--cell-${ex.key}${stageStartClass} ${relevanceClass}`;

                                                        const unitPrice = getUnitPriceFor(cc, sch, ex.key);
                                                        const coefficient = getEffectiveCoefficient(cc, sch, ex.key);
                                                        const quantity = sch ? getQuantityForStage(sch) : 1;

                                                        // Cuando el coeficiente ES el valor, un cero cargado a mano
                                                        // dice que la modalidad no se liquida en esta etapa, y un
                                                        // coeficiente ausente dice que no hay dato: son distintos.
                                                        if (cellMode === 'coefficient') {
                                                            const rawCoefficient = getCoefficientForExecution(sch, ex.key);
                                                            if (rawCoefficient === undefined || rawCoefficient === null || Number.isNaN(rawCoefficient)) {
                                                                return (
                                                                    <td key={`${st.id}-${ex.key}`} className={cellClassName} title={relevanceTitle}>
                                                                        <span className='cgv-muted'>—</span>
                                                                    </td>
                                                                );
                                                            }
                                                            if (rawCoefficient === 0) {
                                                                return (
                                                                    <td key={`${st.id}-${ex.key}`} className={cellClassName} title={`No aplica (${ex.label})`}>
                                                                        <span className='cgv-coef cgv-coef--na'><ProhibitedRegular /></span>
                                                                    </td>
                                                                );
                                                            }
                                                        }

                                                        if (unitPrice === undefined || unitPrice === null) {
                                                            return (
                                                                <td key={`${st.id}-${ex.key}`} className={cellClassName} title={relevanceTitle}>
                                                                    <span className='cgv-muted'>—</span>
                                                                </td>
                                                            );
                                                        }

                                                        const cellCurrency = getAmountCurrency(sch, cc, currency);
                                                        const cellTotal = getExecutionTotalFor(cc, sch, ex.key);
                                                        // La cadena de factores sólo aparece cuando hay algo que
                                                        // explicar: sin coeficiente ni cantidad el total ES el
                                                        // precio unitario, y repetirlo debajo es ruido.
                                                        const showFactors = coefficient !== 1 || quantity !== 1;
                                                        const cellTitle = showFactors
                                                            ? `${relevanceTitle} · ${formatPlainAmount(unitPrice)} × ${formatCoefficient(coefficient)} × ${formatCoefficient(quantity)} = ${formatCurrencyAmount(cellTotal, cellCurrency)}`
                                                            : relevanceTitle;

                                                        return (
                                                            <td key={`${st.id}-${ex.key}`} className={cellClassName} title={cellTitle}>
                                                                <span className='cgv-cell-stack'>
                                                                    <span className='cgv-cell-price'>
                                                                        {formatCurrencyAmount(cellTotal, cellCurrency)}
                                                                    </span>
                                                                    {showFactors && (
                                                                        <span className='cgv-cell-factors'>
                                                                            <span className='cgv-cell-factors__unit'>{formatPlainAmount(unitPrice)}</span>
                                                                            {coefficient !== 1 && <span className='cgv-coef'>x {formatCoefficient(coefficient)}</span>}
                                                                            {quantity !== 1 && <span className='cgv-qty' title={`Cantidad: ${formatCoefficient(quantity)}`}>x {formatCoefficient(quantity)}</span>}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </td>
                                                        );
                                                    });
                                                })}
                                                <td className='cgv-td cgv-td--num cgv-td--quantity'>
                                                    {stageColumns.reduce((accumulator, stage) => {
                                                        const schedule = getStageScheduleFor(cc, stage.id);
                                                        return accumulator + getQuantityForStage(schedule);
                                                    }, 0) || <span className='cgv-muted'>—</span>}
                                                </td>
                                                <td className='cgv-td cgv-td--num cgv-td--total'>
                                                    <CurrencyAmounts
                                                        bold={!isExcluded}
                                                        amounts={stageColumns.reduce((accumulator, stage) => {
                                                            const schedule = getStageScheduleFor(cc, stage.id);
                                                            if (!schedule && !isGroupedInAnyStage(cc)) return accumulator;
                                                            addAmount(
                                                                accumulator,
                                                                getAmountCurrency(schedule, cc, currency),
                                                                getConceptTotalForStage(cc, schedule, stage),
                                                            );
                                                            return accumulator;
                                                        }, new Map() as AmountsByCurrency)}
                                                    />
                                                </td>
                                                <td className='cgv-td cgv-td--actions cgv-sticky-col-right'>
                                                    <Button appearance='subtle' size='small' icon={<EditRegular />} aria-label='Editar' onClick={() => onEdit(cc)} />
                                                    <Button appearance='subtle' size='small' icon={<DeleteRegular />} aria-label='Eliminar' onClick={() => onDelete(cc)} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr className='cgv-tr cgv-tr--total'>
                                        <td className='cgv-td cgv-td--name cgv-sticky-col'>
                                            <div className='cgv-concept'>
                                                <span className='cgv-concept__name'>Precio total por visita</span>
                                                <span className='cgv-concept__ref'>Sólo la modalidad de cada etapa</span>
                                            </div>
                                        </td>
                                        <td className='cgv-td cgv-td--num cgv-sticky-col cgv-sticky-col--2'>
                                            <span className='cgv-muted'>—</span>
                                        </td>
                                        {stageColumns.flatMap(stage => {
                                            const rowExecutions = isCompact
                                                ? allExecutions.filter((ex) =>
                                                    (compactExecutionsByStage.get(stage.id) || []).includes(ex.key))
                                                : allExecutions;

                                            // a compact column can merge phone and remote when the stage
                                            // settles both across different concepts
                                            if (isCompact) {
                                                const merged: AmountsByCurrency = new Map();
                                                let touched = false;
                                                rowExecutions.forEach((ex) => {
                                                    const bucket = totals.relevantStageTotals.get(`${stage.id}-${ex.key}`);
                                                    if (!bucket) return;
                                                    touched = true;
                                                    bucket.forEach((amount, currencyCode) =>
                                                        addAmount(merged, currencyCode || undefined, amount));
                                                });
                                                return [(
                                                    <td
                                                        key={`total-${stage.id}-compact`}
                                                        className={`cgv-td cgv-td--cell cgv-td--cell-${rowExecutions[0]?.key ?? 'inFace'} cgv-cell--stage-start cgv-td--total-cell ${touched ? 'cgv-cell--relevant' : 'cgv-cell--secondary'}`}
                                                    >
                                                        {touched
                                                            ? <CurrencyAmounts amounts={merged} bold />
                                                            : <span className='cgv-muted'>—</span>}
                                                    </td>
                                                )];
                                            }

                                            return allExecutions.map((execution, executionIndex) => {
                                            const key = `${stage.id}-${execution.key}`;
                                            // Cada columna muestra su total; resaltada queda sólo la
                                            // modalidad que liquida la etapa, que es la que suma al
                                            // total general.
                                            const cellTotals = totals.stageTotals.get(key);
                                            const countedTotals = totals.relevantStageTotals.get(key);
                                            const isSettled = countedTotals !== undefined;
                                            // En una etapa remota un concepto puede liquidar telefónico y
                                            // otro remoto: la columna muestra todo, pero al total general
                                            // sólo va una parte. En negrita va únicamente lo que suma.
                                            const countsFully = !!countedTotals && (!cellTotals || sameAmounts(cellTotals, countedTotals));
                                            return (
                                                <td
                                                    key={`total-${key}`}
                                                    className={`cgv-td cgv-td--cell cgv-td--cell-${execution.key}${executionIndex === 0 ? ' cgv-cell--stage-start' : ''} cgv-td--total-cell ${isSettled ? 'cgv-cell--relevant' : 'cgv-cell--secondary'}`}
                                                    title={cellTotals || countedTotals
                                                        ? `${execution.label} — ${countsFully
                                                            ? 'suma al total general'
                                                            : isSettled
                                                                ? 'sólo la parte en negrita suma al total general'
                                                                : 'no suma al total general'}`
                                                        : undefined}
                                                >
                                                    {countsFully && countedTotals
                                                        ? <CurrencyAmounts amounts={countedTotals} bold />
                                                        : cellTotals
                                                            ? (
                                                                <span className='cgv-cell-stack'>
                                                                    <CurrencyAmounts amounts={cellTotals} />
                                                                    {countedTotals && (
                                                                        <span className='cgv-counted'>
                                                                            <span className='cgv-counted__label'>suma</span>
                                                                            <CurrencyAmounts amounts={countedTotals} bold />
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )
                                                            : <span className='cgv-muted'>—</span>}
                                                </td>
                                            );
                                            });
                                        })}
                                        <td className='cgv-td cgv-td--num cgv-td--quantity cgv-td--total-summary'>
                                            {totals.totalQuantity > 0 ? totals.totalQuantity.toLocaleString('es-AR') : <span className='cgv-muted'>—</span>}
                                        </td>
                                        <td className='cgv-td cgv-td--num cgv-td--total cgv-td--total-summary'>
                                            <CurrencyAmounts amounts={totals.grandTotal} bold />
                                        </td>
                                        <td className='cgv-td cgv-td--actions cgv-sticky-col-right'>
                                            <span className='cgv-muted'>—</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
        </section>
    );
};

// ──────────────────────────────────────────────────────────
// Root component
// ──────────────────────────────────────────────────────────
const ConceptGroupedViewComponent = (props: Fn.Props) => {
    const [t] = useTranslation();
    const [isFormOpen, { setTrue: openForm, setFalse: closeForm }] = useBoolean(false);
    const [selectedConcept, setSelectedConcept] = useState<ContractConcept | undefined>(undefined);
    const [globalFilter, setGlobalFilter] = useState('');
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    // Si los opcionales suman a los totales. Por defecto sí, que es como la grilla
    // sumaba siempre. Vive acá y no en la grilla porque la exportación lo necesita:
    // el Excel pone en negrita lo mismo que la página.
    const [includeOptional, setIncludeOptional] = useState<boolean>(true);


    useEffect(() => {
        if (props.Contract) {
            props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
        }
    }, [props.Contract === undefined, props.ContractAddendumId]);

    // Ensure stages are loaded for the current addendum (needed for ByStage matrix columns)
    useEffect(() => {
        const stagesLoaded = Array.isArray(props.Addendum?.stages) && (props.Addendum?.stages?.length || 0) > 0;
        if (props.ContractAddendumId && !stagesLoaded) {
            props.GetAllContractStagesByAddendumId(props.ContractId, props.ContractAddendumId);
        }
    }, [props.ContractId, props.ContractAddendumId, props.Addendum?.stages?.length]);

    // Lazy-load per-stage payment schedules for ALL concepts that don't have them yet
    const requestedScheduleIdsRef = React.useRef<Set<number>>(new Set());
    useEffect(() => {
        if (!props.ContractConcepts || props.ContractConcepts.length === 0) return;
        props.ContractConcepts.forEach(cc => {
            const hasSchedule = Array.isArray(cc.paymentSchedule) && cc.paymentSchedule.length > 0;
            if (hasSchedule) return;
            if (requestedScheduleIdsRef.current.has(cc.id)) return;
            requestedScheduleIdsRef.current.add(cc.id);
            props.GetAllContractsConceptPaymentSchedule(props.ContractId, props.ContractAddendumId, cc.id);
        });
    }, [props.ContractConcepts, props.ContractId, props.ContractAddendumId]);

    const handleAdd = () => {
        setSelectedConcept(undefined);
        openForm();
    };
    const handleEdit = (c: ContractConcept) => {
        setSelectedConcept(c);
        openForm();
    };
    const handleDelete = (c: ContractConcept) => {
        props.DeleteContractConcept(c.id, props.ContractId, props.ContractAddendumId);
    };
    const handleFormDismiss = () => {
        setSelectedConcept(undefined);
        closeForm();
    };

    // Apply global filter once, then bucket
    const globallyFiltered = useMemo(() => {
        const term = globalFilter.trim().toLowerCase();
        const byText = term.length > 0
            ? props.ContractConcepts.filter(c => matchesText(c, term))
            : props.ContractConcepts;

        if (selectedBranchIds.length === 0) {
            return byText;
        }

        return byText.filter((cc) => {
            const conceptBranchIds = getConceptBranchIds(cc);
            if (conceptBranchIds.length === 0) {
                return false;
            }
            const branchIds = new Set(conceptBranchIds);
            return selectedBranchIds.some((id) => branchIds.has(id));
        });
    }, [props.ContractConcepts, globalFilter, selectedBranchIds]);

    const branchOptions: IDropdownOption[] = useMemo(() => {
        const namesById = new Map<number, string>();

        (props.Addendum?.branches || []).forEach((branch) => {
            namesById.set(branch.id, (branch.branchName || '').trim() || `#${branch.id}`);
        });

        (props.ContractConcepts || []).forEach((concept) => {
            (concept.branches || []).forEach((branch) => {
                const id = resolveBranchId(branch);
                if (id === undefined) {
                    return;
                }
                const name = (branch.branchName || '').trim() || namesById.get(id) || `#${id}`;
                if (!namesById.has(id)) {
                    namesById.set(id, name);
                }
            });

            getConceptBranchIds(concept).forEach((id) => {
                if (!namesById.has(id)) {
                    namesById.set(id, `#${id}`);
                }
            });
        });

        return Array.from(namesById.entries())
            .map(([id, name]) => ({ key: id, text: name }))
            .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
    }, [props.Addendum?.branches, props.ContractConcepts]);

    const handleBranchFilterChange = (
        _event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ) => {
        if (!option) {
            return;
        }

        const optionId = Number(option.key);
        setSelectedBranchIds((current) =>
            option.selected
                ? [...current, optionId]
                : current.filter((id) => id !== optionId)
        );
    };

    const buckets = useMemo(() => {
        const byStage: ContractConcept[] = [];
        const inAnyStage: ContractConcept[] = [];
        const others: ContractConcept[] = [];
        for (const c of globallyFiltered) {
            const g = getGroupForConcept(c);
            if (g === 'byStage') byStage.push(c);
            else if (g === 'inAnyStage') inAnyStage.push(c);
            else others.push(c);
        }
        return { byStage, inAnyStage, others };
    }, [globallyFiltered]);

    const stages: ContractStage[] = (props.Addendum?.stages || []) as any;
    const branches: ContractBranch[] = (props.Addendum?.branches || []) as any;

    return (
        <div className='cgv-root'>
            <div className='ConceptListActions cgv-toolbar'>
                <PrimaryButton
                    secondaryText={t('conceptList.componentActions.addConcept')}
                    onClick={handleAdd}
                    text={t('conceptList.componentActions.addConcept')}
                    onRenderIcon={() => <Add24Regular />}
                    className='actionButton'
                />
                <LegacySearchBox
                    className='conceptSearch cgv-global-search'
                    placeholder={t('conceptList.search.placeholder', 'Buscar conceptos por nombre, referencia o descripción')}
                    value={globalFilter}
                    onChange={(_, newValue) => setGlobalFilter(newValue || '')}
                    onClear={() => setGlobalFilter('')}
                    styles={{ root: { maxWidth: 380, margin: '8px 0' } }}
                />
                <Dropdown
                    className='conceptBranchFilter cgv-branch-filter'
                    placeholder={t('conceptList.filters.branchesPlaceholder', 'Filtrar por rama')}
                    selectedKeys={selectedBranchIds}
                    options={branchOptions}
                    onChange={handleBranchFilterChange}
                    multiSelect
                    disabled={branchOptions.length === 0}
                    styles={{ dropdown: { minWidth: 250, margin: '8px 0' } }}
                />
                {/* El archivo baja todas las ramas, no sólo la solapa abierta: es
                    lo que se espera de una exportación y evita tener que ir rama
                    por rama juntando planillas. */}
                <ExcelExportButton
                    disabled={props.ContractConcepts.length === 0}
                    onExport={() =>
                        downloadWorkbook(
                            buildFileName(`conceptos-contrato-${props.Contract?.contractNumber || props.ContractId}`),
                            conceptGridSheets({
                                byStage: buckets.byStage,
                                inAnyStage: buckets.inAnyStage,
                                others: buckets.others,
                                stages,
                                branches,
                                currency: props.Contract?.contractCurrency,
                                includeOptional
                            }),
                            {
                                title: 'Conceptos del contrato',
                                scope: `Contrato ${props.Contract?.contractNumber || props.ContractId}`,
                                note: 'Incluye todas las ramas. Las visitas sin rama aplican a todas y aparecen en cada una.'
                            }
                        )
                    }
                />
                {props.isLoading && (
                    <span className='cgv-loading'>
                        <Spinner size='extra-tiny' /> <span>Cargando…</span>
                    </span>
                )}
            </div>

            {props.failOnLoading && props.error && <ErrorMessageComponent Error={props.error} />}

            <ByStageTable
                title='Conceptos por etapa'
                accent='var(--ichtys-confirmed)'
                concepts={buckets.byStage}
                stages={stages}
                branches={branches}
                currency={props.Contract?.contractCurrency}
                includeOptional={includeOptional}
                onIncludeOptionalChange={setIncludeOptional}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <GenericTable
                title='Conceptos en cualquier etapa'
                icon={<BuildingMultiple20Regular />}
                accent='var(--ichtys-primary)'
                concepts={buckets.inAnyStage}
                currency={props.Contract?.contractCurrency}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <GenericTable
                title='Otros conceptos'
                icon={<AppsListDetail20Regular />}
                accent='var(--ichtys-success)'
                concepts={buckets.others}
                currency={props.Contract?.contractCurrency}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <ConceptFormComponent
                isOpen={isFormOpen}
                openPanel={openForm}
                dismissPanel={handleFormDismiss}
                ContractId={props.ContractId}
                ContractAddendumId={props.ContractAddendumId}
                ContractConceptId={selectedConcept?.id}
                CurrentConcepts={props.Contract?.concepts || []}
                CurrentStages={props.Addendum?.stages || []}
            />
        </div>
    );
};

export default connect(
    Fn.mapStateToProps,
    Fn.mapDispatchToProps,
)(ConceptGroupedViewComponent as any);
